import {
  Component,
  computed,
  effect,
  inject,
  input,
  model,
  signal,
  untracked,
  PLATFORM_ID,
} from '@angular/core';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { DialogModule } from 'primeng/dialog';
import { ButtonModule } from 'primeng/button';
import { CheckboxModule } from 'primeng/checkbox';
import { InputTextModule } from 'primeng/inputtext';
import { TagModule } from 'primeng/tag';

import { CoiService, CoiChannels, CoiSendRequest } from '../../../services/coi';
import { ToastService } from '../../../services/toast';

interface ChannelMeta {
  key: string;
  label: string;
  icon: string;
  desc: string;
  needs: 'email' | 'mobile';
}

/**
 * "Send Certificate of Insurance" dialog — queues the COI to the customer over WhatsApp (PDF),
 * SMS (download link) and/or Email (PDF attachment) via POST api/Coi/Send. The channel gate is
 * SERVER-side and authoritative (the member's partner/client message-type config): the operator's
 * choice can only narrow the enabled set, never widen it, and the messages are QUEUED — actual
 * delivery happens out-of-band, so the UI says "queued for delivery", never "sent".
 */
@Component({
  selector: 'app-send-coi-dialog',
  standalone: true,
  imports: [CommonModule, FormsModule, DialogModule, ButtonModule, CheckboxModule, InputTextModule, TagModule],
  templateUrl: './send-coi-dialog.html',
  styleUrls: ['./send-coi-dialog.css'],
})
export class SendCoiDialog {
  memberId = input<number | null>(null);
  visible = model(false);

  private coi = inject(CoiService);
  private toast = inject(ToastService);
  private platformId = inject(PLATFORM_ID);

  readonly CHANNELS: ChannelMeta[] = [
    { key: 'WHATSAPP', label: 'WhatsApp', icon: 'pi pi-whatsapp', desc: 'PDF document', needs: 'mobile' },
    { key: 'SMS', label: 'SMS', icon: 'pi pi-mobile', desc: 'download link', needs: 'mobile' },
    { key: 'EMAIL', label: 'Email', icon: 'pi pi-envelope', desc: 'PDF attachment', needs: 'email' },
  ];

  info = signal<CoiChannels | null>(null);
  loadingChannels = signal(false);
  /** Channels lookup failed — still allow Send; the server applies the enabled-channel rules. */
  channelsFailed = signal(false);
  sending = signal(false);
  selected = signal<Set<string>>(new Set());

  // Which document(s) to send. Both default on ⇒ certificate + receipt.
  docCoi = signal(true);
  docReceipt = signal(true);

  // Optional recipient overrides (else the member's stored contact is used).
  editEmail = signal(false);
  editMobile = signal(false);
  emailOverride = signal('');
  mobileOverride = signal('');

  /** Dedupe the channels fetch per dialog open (visible flips re-run the effect). */
  private loadedFor = 0;

  constructor() {
    effect(() => {
      const open = this.visible();
      const id = this.memberId();
      if (!isPlatformBrowser(this.platformId)) return;
      if (open && id && id > 0) {
        untracked(() => this.loadChannels(id));
      } else if (!open) {
        this.loadedFor = 0; // next open re-fetches (channel config may have changed)
      }
    });

    // Typing a recipient override makes that channel deliverable server-side — reflect it in
    // the selection too, otherwise the override would be silently ignored (send only requests
    // SELECTED channels, and the backend intersects them with the enabled set).
    effect(() => {
      const inf = this.info();
      if (!inf) return;
      const en = new Set(inf.enabledChannels);
      const addEmail = this.editEmail() && !!this.emailOverride().trim() && en.has('EMAIL');
      const addMobile = this.editMobile() && !!this.mobileOverride().trim();
      if (!addEmail && !addMobile) return;
      this.selected.update((s) => {
        const n = new Set(s);
        if (addEmail) n.add('EMAIL');
        if (addMobile) {
          if (en.has('SMS')) n.add('SMS');
          if (en.has('WHATSAPP')) n.add('WHATSAPP');
        }
        return n;
      });
    });
  }

  private readonly enabled = computed(() => new Set(this.info()?.enabledChannels ?? []));
  readonly visibleChannels = computed(() => this.CHANNELS.filter((c) => this.enabled().has(c.key)));
  readonly noneEnabled = computed(() => !!this.info() && this.enabled().size === 0);

  /** A channel is deliverable when enabled AND a matching contact exists (stored or overridden). */
  isDeliverable(key: string): boolean {
    const inf = this.info();
    if (!inf || !this.enabled().has(key)) return false;
    const meta = this.CHANNELS.find((c) => c.key === key);
    const hasOverride =
      meta?.needs === 'email'
        ? !!(this.editEmail() && this.emailOverride().trim())
        : !!(this.editMobile() && this.mobileOverride().trim());
    return hasOverride || (meta?.needs === 'email' ? inf.hasEmail : inf.hasMobile);
  }

  /** Checked channels that are currently deliverable — what Send will actually request. */
  readonly effectiveSelection = computed(() => {
    // computed() can't call isDeliverable() lazily per-signal, so re-derive here:
    const inf = this.info();
    if (!inf) return [] as string[];
    const enabled = this.enabled();
    const emailOk = (this.editEmail() && !!this.emailOverride().trim()) || inf.hasEmail;
    const mobileOk = (this.editMobile() && !!this.mobileOverride().trim()) || inf.hasMobile;
    return [...this.selected()].filter(
      (k) => enabled.has(k) && (k === 'EMAIL' ? emailOk : mobileOk),
    );
  });

  readonly canSend = computed(() => {
    if (this.sending() || this.loadingChannels()) return false;
    if (!this.docCoi() && !this.docReceipt()) return false;   // pick at least one document
    if (this.channelsFailed()) return true; // server still enforces everything
    const inf = this.info();
    return !!inf && inf.issued && this.effectiveSelection().length > 0;
  });

  private loadChannels(memberId: number) {
    if (this.loadedFor === memberId) return;
    this.loadedFor = memberId;
    this.loadingChannels.set(true);
    this.channelsFailed.set(false);
    this.info.set(null);
    this.selected.set(new Set());
    this.docCoi.set(true);
    this.docReceipt.set(true);
    this.editEmail.set(false);
    this.editMobile.set(false);
    this.emailOverride.set('');
    this.mobileOverride.set('');
    this.coi.getCoiChannels(memberId).subscribe({
      next: (res: any) => {
        if (this.loadedFor !== memberId) return; // a newer open superseded this request
        this.loadingChannels.set(false);
        if (res?.success === false || !res?.data) {
          this.channelsFailed.set(true);
          this.toast.warning(res?.message || "Couldn't check this member's channels.");
          return;
        }
        const inf = res.data as CoiChannels;
        this.info.set(inf);
        this.selected.set(new Set(inf.deliverable ?? [])); // default-check every deliverable channel
      },
      error: (err: any) => {
        if (this.loadedFor !== memberId) return; // stale response for a previous member
        this.loadingChannels.set(false);
        this.channelsFailed.set(true);
        this.toast.warning(err?.error?.message || "Couldn't check this member's channels.");
      },
    });
  }

  toggleChannel(key: string, on: boolean) {
    this.selected.update((s) => {
      const n = new Set(s);
      on ? n.add(key) : n.delete(key);
      return n;
    });
  }

  close() {
    if (this.sending()) return;
    this.visible.set(false);
  }

  send() {
    const id = this.memberId();
    if (!id || this.sending()) return;

    const inf = this.info();
    const body: CoiSendRequest = { memberId: id, coi: this.docCoi(), receipt: this.docReceipt() };

    if (inf) {
      const chosen = this.effectiveSelection();
      if (!chosen.length) {
        this.toast.warning('Pick at least one channel.');
        return;
      }
      // Leaving every enabled channel checked ⇒ omit `channels` (server sends on all enabled).
      const allEnabledChosen = inf.enabledChannels.every((c) => this.selected().has(c));
      if (!allEnabledChosen) body.channels = chosen;
    }
    const email = this.editEmail() ? this.emailOverride().trim() : '';
    const mobile = this.editMobile() ? this.mobileOverride().trim() : '';
    if (email) body.email = email;
    if (mobile) body.mobile = mobile;

    this.sending.set(true);
    this.coi.sendCoi(body).subscribe({
      next: (res: any) => {
        this.sending.set(false);
        // Defensive only — CoiController returns REAL HTTP statuses, so failures normally land
        // in the error callback below; this guards against the app-wide 200+success:false drift.
        if (res?.success === false) {
          if (res?.statusCode === 409) this.toast.info(res?.message || 'Already queued a moment ago.');
          else this.toast.error(res?.message || 'Could not queue the certificate.');
          return;
        }
        // The server message already combines queued + skipped ("…queued for delivery on: X. Skipped: Y.").
        const d = res?.data;
        this.toast.success(
          res?.message ||
            (d?.channels?.length
              ? `Certificate queued for delivery on: ${d.channels.join(', ')}.`
              : 'Certificate queued for delivery.'),
        );
        this.visible.set(false);
      },
      error: (err: any) => {
        this.sending.set(false);
        // Unlike most controllers, api/Coi/Send returns real HTTP statuses (400/404/409), so
        // this is the primary failure path. errorInterceptor deliberately skips /Coi/ toasts.
        if (err?.status === 409) {
          // Same member+channel already queued (PENDING) — nothing more to do here.
          this.toast.info(err?.error?.message || 'Already queued a moment ago.');
          this.visible.set(false);
        } else {
          // 400 (not issued / no enabled or deliverable channel) / 404 / 500 — keep the dialog
          // open so the operator can adjust the channels or overrides and retry.
          this.toast.error(err?.error?.message || 'Could not queue the certificate.');
        }
      },
    });
  }
}
